import { useState } from 'react'

import { Card } from '@/components/ui'
import { formatDate, numberToCurrency as money } from '@/utils'
import { AccountHeading } from './detail/AccountHeading'
import type { AccountSection } from './detail/detailScope'
import { GROUP_DIVIDER, GROUPS_SHOWN } from './detail/groupLayout'
import { PageLink } from './detail/PageLink'
import { ShowAllButton } from './detail/ShowAllButton'
import { countOf } from './portfolioText'
import type { UnvestedAwardRow } from './types'
import { sumOrNull } from './usePortfolioDashboard'

interface UnvestedAwardsProps {
  sections: AccountSection[]
  showNames: boolean
}

const quantity = (value: number | null) =>
  value == null
    ? ''
    : value.toLocaleString('en-US', { maximumFractionDigits: 4 })

const allSame = <Value,>(values: Value[]) =>
  values.every((value) => value === values[0])

// "All six are AAPL RSUs, granted at $0.00 and valued at $129.93 a share." Said only when true of every award.
const sharedTerms = (awards: UnvestedAwardRow[]): string => {
  const [first] = awards
  const isUniform =
    awards.length > 1 &&
    allSame(awards.map((award) => award.symbol)) &&
    allSame(awards.map((award) => award.awardType)) &&
    allSame(awards.map((award) => award.grantPrice)) &&
    allSame(awards.map((award) => award.marketPrice))
  if (!isUniform || !first.symbol || first.marketPrice == null) return ''
  const granted =
    first.grantPrice == null ? '' : `, granted at ${money(first.grantPrice)}`
  const kind = first.awardType ? ` ${first.awardType}` : ''
  return ` All ${awards.length} are ${first.symbol}${kind} awards${granted} and valued at ${money(first.marketPrice)} a share.`
}

const AwardsTotal = ({ awards }: { awards: UnvestedAwardRow[] }) => {
  const total = sumOrNull(awards.map((award) => award.marketValue))
  return (
    <span className="flex items-baseline gap-2 text-xs text-ink-subtle">
      Total of {countOf(awards.length, 'award', 'awards')}
      <span className="text-sm tabular-nums text-blue-7">
        {total == null ? 'Not printed for every award' : money(total)}
      </span>
    </span>
  )
}

const AwardsTable = ({ awards }: { awards: UnvestedAwardRow[] }) => (
  <table className="mt-4 w-full table-fixed border-collapse text-sm">
    <thead>
      <tr className="border-b text-left text-xs text-ink-subtle">
        <th className="w-32 pb-2.5 font-normal">Grant date</th>
        <th className="pb-2.5 font-normal">Award number</th>
        <th className="w-32 pb-2.5 text-right font-normal">Quantity</th>
        <th className="w-40 pb-2.5 text-right font-normal">Value, pre-tax</th>
        <th className="w-24 pb-2.5" />
      </tr>
    </thead>
    <tbody className="divide-y tabular-nums">
      {awards.map((award) => (
        <tr key={award.id}>
          <td className="py-2.5">
            {award.grantDate ? formatDate(award.grantDate) : 'Not printed'}
          </td>
          <td className="truncate py-2.5">{award.awardNumber}</td>
          <td className="py-2.5 text-right">{quantity(award.quantity)}</td>
          <td className="py-2.5 text-right">
            {award.marketValue == null
              ? 'Not printed'
              : money(award.marketValue)}
          </td>
          <td className="py-2.5 text-right">
            <PageLink
              fileUrl={award.statementFileUrl}
              page={award.sourcePage}
            />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
)

// Unvested employee stock awards, one group per account. Never part of the account value.
export const UnvestedAwards = ({
  sections,
  showNames,
}: UnvestedAwardsProps) => {
  const [showAll, setShowAll] = useState(false)
  const withAwards = sections.filter((section) => section.awards.length > 0)
  if (withAwards.length === 0) return null
  const shown = showAll ? withAwards : withAwards.slice(0, GROUPS_SHOWN)
  const everyAward = withAwards.flatMap((section) => section.awards)
  return (
    <Card className="p-6 text-sm">
      <h3 className="text-base font-semibold">Unvested stock awards</h3>
      <p className="mt-3 text-sm text-ink-subtle">
        Not part of the account value.{sharedTerms(everyAward)}
      </p>
      {shown.map((section, position) => (
        <div
          key={section.account.accountId}
          className={position === 0 ? 'mt-6' : GROUP_DIVIDER}
        >
          <AccountHeading
            section={section}
            showName={showNames}
            summary={<AwardsTotal awards={section.awards} />}
          />
          <AwardsTable awards={section.awards} />
        </div>
      ))}
      {!showAll && withAwards.length > GROUPS_SHOWN && (
        <div className="mt-4">
          <ShowAllButton
            count={withAwards.length}
            noun="accounts with awards"
            onClick={() => setShowAll(true)}
          />
        </div>
      )}
    </Card>
  )
}
